import React, { useState, useEffect } from 'react';
import { Modal, Button, Group, Text, TextInput, Stack, Select, PasswordInput, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconUser, IconPlus } from '@tabler/icons-react';
import type { Department, Role } from '../services/permissionApi';
import { usePermissionControl } from '../hooks/usePermissionControl';
import { isCompanyLevelRole, NO_DEPARTMENT_ID } from '../utils';

interface AddUserDialogProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (userData: {
    name: string;
    email: string;
    password: string;
    departmentId?: string;
    sysRoleId?: number;
  }) => Promise<boolean>;
  departments: Department[];
  roles: Role[];
  loadAssignableRoles: () => Promise<Role[]>;
  organizationData: any[];
}

// 获取所有部门列表（与MemberEditDialogs保持一致）
const getAllDepartments = (nodes: any[], path: string[] = []): Array<{ path: string; id: string; isLeaf: boolean }> => {
  let departments: Array<{ path: string; id: string; isLeaf: boolean }> = [];
  for (const node of nodes) {
    // 如果是根节点（通常id为'root'），直接用node.name作为起始路径
    // 否则，继承父路径
    const currentPath = node.id === 'root' ? [node.name] : [...path, node.name];
    const pathString = currentPath.join(' > ');
    const isLeaf = !node.children || node.children.length === 0;
    // 根节点也要添加到部门列表中
    departments.push({ path: pathString, id: node.id, isLeaf });
    if (node.children && node.children.length > 0) {
      departments = departments.concat(getAllDepartments(node.children, currentPath));
    }
  }
  return departments;
};

// 过滤可选择的部门
const getFilteredDepartments = (
  allDepartments: Array<{ path: string; id: string; isLeaf: boolean }>,
  permissionControl: any
) => {
  // 公司所有者和管理员可以选择所有部门
  if (permissionControl.isOwner || permissionControl.isAdmin) {
    return allDepartments;
  }

  // 部门管理员只能选择自己所属部门及其子部门
  if (permissionControl.currentUserRole === 'ROLE_ADMIN_DEPARTMENT') {
    const currentUserPath = permissionControl.currentUser?.departmentPath;
    if (!currentUserPath) return [];

    return allDepartments.filter((dept) => {
      // 根节点不可选择
      if (dept.id === 'root') return false;

      // 检查部门是否是当前用户部门或其子部门
      return dept.path.startsWith(currentUserPath);
    });
  }

  return [];
};

const AddUserDialog: React.FC<AddUserDialogProps> = ({
  opened,
  onClose,
  onSubmit,
  departments,
  roles,
  loadAssignableRoles,
  organizationData,
}) => {
  const { t } = useTranslation();
  const permissionControl = usePermissionControl();
  const [assignableRoles, setAssignableRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const form = useForm({
    initialValues: {
      name: '',
      email: '',
      password: '',
      departmentPath: '',
      sysRoleId: '',
    },
    validate: {
      name: (value) => {
        if (!value?.trim()) {
          return t('adminConsole.newConsole.addUser.validation.nameRequired') || '请输入用户名';
        }
        if (value.trim().length < 2) {
          return t('adminConsole.newConsole.addUser.validation.nameMinLength') || '用户名至少需要2个字符';
        }
        return null;
      },
      email: (value) => {
        if (!value?.trim()) {
          return t('adminConsole.newConsole.addUser.validation.emailRequired') || '请输入邮箱地址';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim())) {
          return t('adminConsole.newConsole.addUser.validation.emailFormat') || '请输入有效的邮箱地址';
        }
        return null;
      },
      password: (value) => {
        if (!value?.trim()) {
          return t('adminConsole.newConsole.addUser.validation.passwordRequired') || '请输入密码';
        }
        if (value.trim().length < 6) {
          return t('adminConsole.newConsole.addUser.validation.passwordMinLength') || '密码至少需要6个字符';
        }
        return null;
      },
      departmentPath: (value, values) => {
        // 公司级角色（公司所有者 / 公司管理员）可不选部门
        const selectedRole = assignableRoles.find((r) => r.id.toString() === values.sysRoleId);
        if (isCompanyLevelRole(selectedRole?.code)) {
          return null;
        }
        if (!value?.trim()) {
          return t('adminConsole.newConsole.member.departmentRequired');
        }
        return null;
      },
      sysRoleId: (value) => {
        if (!value?.trim()) {
          return t('adminConsole.newConsole.addUser.validation.roleRequired') || '请选择权限等级';
        }
        return null;
      },
    },
  });

  // 重置表单当对话框关闭时
  useEffect(() => {
    if (!opened) {
      form.reset();
      setAssignableRoles([]);
    }
  }, [opened]);

  // 加载可分配角色当对话框打开时
  useEffect(() => {
    if (opened) {
      const loadRoles = async () => {
        setLoadingRoles(true);
        try {
          const roles = await loadAssignableRoles();
          setAssignableRoles(roles);
        } catch (error) {
          console.error('加载可分配角色失败:', error);
          setAssignableRoles([]);
        } finally {
          setLoadingRoles(false);
        }
      };
      loadRoles();
    }
  }, [opened, loadAssignableRoles]);

  // 获取所有部门（与MemberEditDialogs保持一致）
  const allDepartments = getAllDepartments(organizationData);

  // 根据权限过滤部门
  const filteredDepartments = getFilteredDepartments(allDepartments, permissionControl);

  // 准备部门选项
  const departmentOptions = filteredDepartments.map((d) => ({
    value: d.path,
    label: d.path,
  }));

  // 当前选中角色是否为公司级角色（公司所有者 / 公司管理员）——此类角色部门可不选
  const selectedRoleCode = assignableRoles.find((r) => r.id.toString() === form.values.sysRoleId)?.code;
  const departmentOptional = isCompanyLevelRole(selectedRoleCode);

  // 准备角色选项
  const roleOptions = [
    { value: '', label: t('adminConsole.newConsole.addUser.selectRole') || '请选择权限等级' },
    ...assignableRoles.map((role) => ({
      value: role.id.toString(),
      label: role.name,
    })),
  ];

  const handleSubmit = async (values: typeof form.values) => {
    // 通过部门路径找到对应的 departmentId（与MemberEditDialogs保持一致）
    let departmentId: string | undefined = undefined;
    if (values.departmentPath) {
      const department = filteredDepartments.find((d) => d.path === values.departmentPath);

      // 排除根节点，确保只传递实际部门的ID
      if (department && department.id !== 'root') {
        departmentId = department.id;
      }
    }

    // 公司级角色（公司所有者 / 公司管理员）允许不选部门，此时部门 ID 传 0
    if (!departmentId) {
      const selectedRole = assignableRoles.find((r) => r.id.toString() === values.sysRoleId);
      if (isCompanyLevelRole(selectedRole?.code)) {
        departmentId = NO_DEPARTMENT_ID;
      }
    }

    const userData = {
      name: values.name.trim(),
      email: values.email.trim(),
      password: values.password.trim(),
      departmentId,
      sysRoleId: values.sysRoleId ? parseInt(values.sysRoleId) : undefined,
    };

    const success = await onSubmit(userData);

    if (success) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <IconPlus size={20} />
          <Text fw={500}>{t('adminConsole.newConsole.addUser.title') || '添加用户'}</Text>
        </Group>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              {t('adminConsole.newConsole.addUser.description') ||
                '创建新用户账号，用户将收到账号信息并可立即登录系统。'}
            </Text>
          </Alert>

          <TextInput
            label={t('adminConsole.newConsole.addUser.form.name') || '用户名'}
            placeholder={t('adminConsole.newConsole.addUser.form.namePlaceholder') || '请输入用户姓名'}
            required
            maxLength={40}
            {...form.getInputProps('name')}
          />

          <TextInput
            label={t('adminConsole.newConsole.addUser.form.email') || '邮箱地址'}
            placeholder={t('adminConsole.newConsole.addUser.form.emailPlaceholder') || '请输入邮箱地址'}
            required
            {...form.getInputProps('email')}
          />

          <PasswordInput
            label={t('adminConsole.newConsole.addUser.form.password') || '密码'}
            placeholder={t('adminConsole.newConsole.addUser.form.passwordPlaceholder') || '请输入密码（至少6位）'}
            required
            {...form.getInputProps('password')}
          />

          <Select
            label={t('adminConsole.newConsole.addUser.form.department') || '所属部门'}
            placeholder={t('adminConsole.newConsole.addUser.form.departmentPlaceholder') || '请选择所属部门'}
            data={departmentOptions}
            searchable
            clearable={departmentOptional}
            required={!departmentOptional}
            {...form.getInputProps('departmentPath')}
          />

          <Select
            label={t('adminConsole.newConsole.addUser.form.role') || '权限等级'}
            placeholder={
              loadingRoles
                ? '正在加载角色...'
                : t('adminConsole.newConsole.addUser.form.rolePlaceholder') || '请选择权限等级'
            }
            data={roleOptions}
            disabled={loadingRoles}
            required
            {...form.getInputProps('sysRoleId')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={onClose}>
              {t('adminConsole.newConsole.addUser.form.cancel') || '取消'}
            </Button>
            <Button type="submit" leftSection={<IconPlus size={16} />}>
              {t('adminConsole.newConsole.addUser.form.submit') || '创建用户'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default AddUserDialog;
